import {
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
  type Dispatch,
  type SetStateAction,
} from "react";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { useNavigate } from "@tanstack/react-router";
import { Filter } from "lucide-react";
import { Task, List, Tag, TaskFilter, NewTask, BuiltInFilterState } from "../../types";
import { useDialog } from "../../contexts/DialogContext";
import { useView } from "../../contexts/ViewContext";
import { useUIPreferences } from "../../contexts/UIPreferencesContext";
import { useAuth } from "../../contexts/AuthContext";
import { useListMembers } from "../../hooks/queries/useListMembers";
import { useDeleteFilter } from "../../hooks/mutations/useDeleteFilter";
import { useUpdateFilterOrder } from "../../hooks/mutations/useUpdateFilterOrder";
import { useUpdateTask } from "../../hooks/mutations/useUpdateTask";
import { useUpdateTaskOrder } from "../../hooks/mutations/useUpdateTaskOrder";
import { useBoardFilter } from "../../hooks/useBoardFilter";
import { useTaskDragDrop } from "../../hooks/useTaskDragDrop";
import { useBoardNavigation } from "../../hooks/useBoardNavigation";
import { useBoardVisibleTasks } from "../../hooks/useBoardVisibleTasks";
import { useCollapsedLists } from "../../hooks/useCollapsedLists";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts";
import { EMPTY_LIST_MEMBERS } from "../../lib/listSharing";
import {
  cycleScheduledFilters,
  serializeScheduledFilters,
} from "../../lib/scheduledFilters";
import FilterRow from "../filter/FilterRow";
import FilterNotFoundBanner from "../filter/FilterNotFoundBanner";
import InvitationToasts from "../misc/InvitationToasts";
import EmptyState from "../misc/EmptyState";
import WelcomeEmptyState from "../misc/WelcomeEmptyState";
import BoardColumnLayout from "./BoardColumnLayout";
import ReorderListsDialog from "../dialogs/ReorderListsDialog";

export interface BoardViewProps {
  // Data
  tasks: Task[];
  lists: List[];
  tags: Tag[];
  filters: TaskFilter[];

  // Dialog
  dialog: ReturnType<typeof useDialog>;
  showEditDialog: boolean;
  /** Setter owned by TodoApp — syncs with the FilterDialog rendered there */
  setEditingFilter: Dispatch<SetStateAction<TaskFilter | null>>;
  buildListBreakdown: (
    listName: string,
  ) => { open: number; completed: number; archived: number };

  // Task editing (callbacks into parent)
  handleEditTask: (task: Task) => void;
  exitAnimatingTaskId: string | null;
  setConvertingTaskId: Dispatch<SetStateAction<string | null>>;

  /** Owned by TodoApp: completing a task with open subtasks opens a
   *  confirmation dialog instead of writing, so this stays a parent callback. */
  toggleTask: (id: string) => Promise<void>;

  // Task creation
  setNewTask: Dispatch<SetStateAction<NewTask>>;
  quickAddTask: (title: string) => Promise<void>;

  /** Called whenever the ordered visible-task list changes — TodoApp feeds
   *  this into useTaskEditing so prev/next navigation works from any view. */
  onVisibleTasksChange: (tasks: Task[]) => void;
}

export default function BoardView({
  tasks,
  lists,
  tags,
  filters,
  dialog,
  showEditDialog,
  setEditingFilter,
  buildListBreakdown,
  handleEditTask,
  exitAnimatingTaskId,
  setConvertingTaskId,
  toggleTask,
  setNewTask,
  quickAddTask,
  onVisibleTasksChange,
}: BoardViewProps) {
  const {
    activeFilterId,
    setActiveFilterId,
    builtInFilterState,
    clearFilters,
    searchTerm,
    setSearchTerm,
    sortByPriority,
    sortByCreatedDate,
    showCompleted,
    showArchived,
  } = useView();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate() as any;
  const isMobile = useIsMobile();
  const { preferences, setPreferences } = useUIPreferences();
  const { user } = useAuth();
  const { data: listMemberRows } = useListMembers();
  const listMembers = listMemberRows ?? EMPTY_LIST_MEMBERS;
  const listlessPlacement = preferences.listlessGroupPlacement;
  const deleteFilter = useDeleteFilter();
  const updateFilterOrder = useUpdateFilterOrder();
  const updateTask = useUpdateTask();
  const updateTaskOrder = useUpdateTaskOrder();

  const isNoDialogOpen = useCallback(
    () => dialog.current === null && !showEditDialog,
    [dialog, showEditDialog],
  );

  // ── URL filter helpers ────────────────────────────────────────────────────

  const applyBuiltInFilter = useCallback(
    (state: BuiltInFilterState) => {
      const params: Record<string, string | undefined> = {
        priorities:
          state.priorities.length > 0
            ? state.priorities.join(",")
            : undefined,
        lists:
          state.listIds.length > 0
            ? state.listIds.map((id) => id ?? "none").join(",")
            : undefined,
        tags: state.tagIds.length > 0 ? state.tagIds.join(",") : undefined,
        scheduled: serializeScheduledFilters(state.scheduledFilter),
        work_hours: state.workHoursFilter ?? undefined,
      };
      navigate({
        search: () => params as Record<string, unknown>,
        replace: true,
      });
    },
    [navigate],
  );

  const handleAddFilter = useCallback(() => {
    setEditingFilter(null);
    dialog.open("filter");
  }, [setEditingFilter, dialog]);

  const handleEditFilter = useCallback(
    (filter: TaskFilter) => {
      setEditingFilter(filter);
      dialog.open("filter");
    },
    [setEditingFilter, dialog],
  );

  const handleDeleteFilter = useCallback(
    async (filterId: string) => {
      try {
        await deleteFilter.mutateAsync(filterId);
      } catch {
        // The hook rolls the cache back and reports the failure via Sentry and
        // a toast. The view still leaves a deleted-but-active filter, matching
        // the swallowed `false` the previous parent adapter returned.
      }
      if (activeFilterId === filterId) {
        setActiveFilterId(null);
      }
    },
    [deleteFilter, activeFilterId, setActiveFilterId],
  );

  const handleReorderFilters = useCallback(
    (filterId: string, newOrderPosition: number) => {
      updateFilterOrder.mutate({ filterId, newOrderPosition });
    },
    [updateFilterOrder],
  );

  // Shared by the "1"/"2" prev/next shortcuts below.
  const cycleFilter = useCallback(
    (delta: number) => {
      if (filters.length === 0) return;
      if (!activeFilterId) {
        setActiveFilterId(filters[0].id);
        return;
      }
      const currentIndex = filters.findIndex((f) => f.id === activeFilterId);
      if (currentIndex === -1) return;
      const nextIndex = (currentIndex + delta + filters.length) % filters.length;
      setActiveFilterId(filters[nextIndex].id);
    },
    [filters, activeFilterId, setActiveFilterId],
  );

  // Drag-and-drop writes. Both are fire-and-forget — the drag handlers never
  // read a result — so they call mutate() rather than mutateAsync().
  const handleDragUpdateTask = useCallback(
    (id: string, updates: Partial<Task>) => {
      updateTask.mutate({ id, updates });
    },
    [updateTask],
  );

  const handleDragUpdateTaskOrder = useCallback(
    (taskId: string, newOrderPosition: number) => {
      updateTaskOrder.mutate({ taskId, newOrderPosition });
    },
    [updateTaskOrder],
  );

  const handleAddToColumn = useCallback(
    (_columnName: string, listId?: string) => {
      setNewTask((prev) => ({ ...prev, list_id: listId || null }));
      dialog.open("add-task", null);
    },
    [setNewTask, dialog],
  );

  // ── Board data ────────────────────────────────────────────────────────────

  const { columns, listlessTasks, matchingTaskIds, isFilterActive } =
    useBoardFilter({
      tasks,
      lists,
      filters,
      showArchived,
      showCompleted,
      searchTerm,
      activeFilterId,
      builtInFilterState,
      sortByPriority,
      sortByCreatedDate,
      currentUserId: user?.id,
      listMembers,
    });

  const collapse = useCollapsedLists({
    isMobile,
    isSearching: searchTerm.trim() !== "",
    lists,
  });

  const {
    sensors,
    collisionDetection,
    sortingStrategy,
    activeDragTask,
    overTaskId,
    overEndZoneListId,
    allSortableIds,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  } = useTaskDragDrop({
    columns,
    listlessTasks,
    matchingTaskIds,
    isCollapsed: collapse.isCollapsed,
    updateTask: handleDragUpdateTask,
    updateTaskOrder: handleDragUpdateTaskOrder,
  });

  const nonEmptyListIds = useMemo(() => {
    const set = new Set<string>();
    for (const t of tasks) {
      if (!t.archived && !t.completed && t.list_id) set.add(t.list_id);
    }
    return set;
  }, [tasks]);

  const filterNotFound =
    !!activeFilterId &&
    filters.length > 0 &&
    !filters.find((f) => f.id === activeFilterId);

  const visibleTasks = useBoardVisibleTasks({
    columns,
    listlessTasks,
    matchingTaskIds,
    listlessPlacement,
  });

  // Skip the parent update when the same tasks are already reported. A new
  // array with the same task objects used to schedule another render, and
  // that render built another array, until React stopped the page. Layout,
  // not a passive effect, so the dialog snapshots this list in the same turn.
  const reportedVisibleTasks = useRef<Task[] | null>(null);
  useLayoutEffect(() => {
    const previous = reportedVisibleTasks.current;
    const sameTasks =
      previous !== null &&
      previous.length === visibleTasks.length &&
      previous.every((task, index) => task === visibleTasks[index]);
    if (sameTasks) return;
    reportedVisibleTasks.current = visibleTasks;
    onVisibleTasksChange(visibleTasks);
  }, [visibleTasks, onVisibleTasksChange]);

  const { focusedTaskId } = useBoardNavigation({
    visibleTasks,
    viewType: "board",
    isNoDialogOpen,
    isMobile,
    activeDragTask,
    openTask: (taskId) => {
      const task = tasks.find((t) => t.id === taskId);
      if (task) handleEditTask(task);
    },
  });

  // ── Board-specific keyboard shortcuts ─────────────────────────────────────

  useKeyboardShortcuts([
    // "s" — one schedule choice steps forward. Several choices turn the filter off.
    // Calendar uses S to show or hide the side list.
    {
      key: "S",
      when: isNoDialogOpen,
      run: () => {
        applyBuiltInFilter({
          ...builtInFilterState,
          scheduledFilter: cycleScheduledFilters(builtInFilterState.scheduledFilter),
        });
      },
    },

    // "t" — cycle tag filter (board only; calendar has its own T = jump to today)
    {
      key: "T",
      when: isNoDialogOpen,
      run: () => {
        if (tags.length === 0) return;
        const currentTags = builtInFilterState.tagIds;
        if (currentTags.length === 0) {
          applyBuiltInFilter({ ...builtInFilterState, tagIds: [tags[0].id] });
        } else if (currentTags.length === 1) {
          const idx = tags.findIndex((t) => t.id === currentTags[0]);
          if (idx >= 0 && idx < tags.length - 1) {
            applyBuiltInFilter({
              ...builtInFilterState,
              tagIds: [tags[idx + 1].id],
            });
          } else {
            applyBuiltInFilter({ ...builtInFilterState, tagIds: [] });
          }
        } else {
          applyBuiltInFilter({ ...builtInFilterState, tagIds: [] });
        }
      },
    },

    // "1" / "2" — previous/next custom filter
    { key: "1", when: isNoDialogOpen, run: () => cycleFilter(-1) },
    { key: "2", when: isNoDialogOpen, run: () => cycleFilter(1) },

    // "r" — reorder lists dialog (desktop board only)
    {
      key: "R",
      when: () => isNoDialogOpen() && !isMobile,
      run: () => dialog.open("reorder-lists"),
    },
  ]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <InvitationToasts listMembers={listMembers} lists={lists} />

      {tasks.length > 0 && (
        <FilterRow
          filters={filters}
          onAddFilter={handleAddFilter}
          onEditFilter={handleEditFilter}
          onDeleteFilter={handleDeleteFilter}
          onReorderFilters={handleReorderFilters}
          lists={lists}
          tags={tags}
          nonEmptyListIds={nonEmptyListIds}
        />
      )}

      {filterNotFound && <FilterNotFoundBanner />}

      <div className="p-2 sm:p-4">
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={allSortableIds} strategy={sortingStrategy}>
            {isFilterActive && matchingTaskIds.size === 0 && (
              <EmptyState
                icon={Filter}
                title="No matches"
                description="No tasks match your current filters"
                ctaLabel="Clear filters"
                onCtaClick={() => {
                  setSearchTerm("");
                  clearFilters();
                }}
              />
            )}
            {columns.every((c) => c.tasks.length === 0) &&
              listlessTasks.length === 0 &&
              !isFilterActive && (
                <WelcomeEmptyState
                  onCreateTask={() => dialog.open("add-task", null)}
                  onQuickAdd={quickAddTask}
                />
              )}
            <BoardColumnLayout
              columns={columns}
              listlessTasks={listlessTasks}
              matchingTaskIds={matchingTaskIds}
              listlessPlacement={listlessPlacement}
              isMobile={isMobile}
              activeDragTask={activeDragTask}
              overTaskId={overTaskId}
              overEndZoneListId={overEndZoneListId}
              exitAnimatingTaskId={exitAnimatingTaskId}
              focusedTaskId={focusedTaskId}
              isCollapsed={collapse.isCollapsed}
              onToggleCollapse={
                collapse.canCollapse ? collapse.toggleListCollapsed : undefined
              }
              onAddToColumn={handleAddToColumn}
              onToggleTask={toggleTask}
              onEditTask={handleEditTask}
              onConvertToSubtask={setConvertingTaskId}
              onDeleteList={(listName) => {
                const breakdown = buildListBreakdown(listName);
                dialog.open("delete-confirm", {
                  type: "list",
                  name: listName,
                  taskCount:
                    breakdown.open +
                    breakdown.completed +
                    breakdown.archived,
                  listBreakdown: breakdown,
                });
              }}
            />
          </SortableContext>

          <DragOverlay dropAnimation={null}>
            {activeDragTask ? (
              <div className="bg-warm-bg-card border border-warm-border rounded-card p-3 shadow-lg opacity-95 max-w-[280px]">
                <p className="text-sm text-warm-text-primary font-medium line-clamp-2">
                  {activeDragTask.title}
                </p>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {dialog.isOpen("reorder-lists") && (
        <ReorderListsDialog
          lists={lists}
          listlessCount={
            tasks.filter(
              (t) => !t.list_id && !t.archived && !t.completed,
            ).length
          }
          placement={listlessPlacement}
          onPlacementChange={(placement) =>
            setPreferences({ listlessGroupPlacement: placement })
          }
          onClose={() => dialog.close()}
        />
      )}
    </>
  );
}
